export const MYCASE_ACTIVITY_METADATA_KEY = "mycase_activity";
export const INITIAL_DESCRIPTION_METADATA_KEY = "initial_description";

export const normalizeTimeEntryMetadata = (metadata) => {
  if (!metadata) return {};

  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? { ...parsed }
        : {};
    } catch (error) {
      return {};
    }
  }

  if (typeof metadata === "object" && !Array.isArray(metadata)) {
    return { ...metadata };
  }

  return {};
};

export const getMyCaseActivityFromMetadata = (metadata) => {
  const normalized = normalizeTimeEntryMetadata(metadata);
  const mycase = normalized.mycase && typeof normalized.mycase === "object"
    ? normalized.mycase
    : {};
  const value = [
    normalized[MYCASE_ACTIVITY_METADATA_KEY],
    normalized.mycaseActivity,
    mycase.activity,
    mycase.activity_name,
    normalized.activity,
  ].find(item => String(item ?? "").trim());

  return value === undefined || value === null ? "" : String(value);
};

export const getMyCaseActivityFromTimeEntry = (timeEntry) => {
  const metadataValue = getMyCaseActivityFromMetadata(timeEntry?.metadata);
  if (metadataValue) return metadataValue;

  const relatedMetadataValue = getMyCaseActivityFromMetadata(
    timeEntry?.relateTimeEntry?.metadata ||
    timeEntry?.relatedTimeEntry?.metadata ||
    timeEntry?.timeEntry?.metadata
  );
  if (relatedMetadataValue) return relatedMetadataValue;

  const mycase = timeEntry?.mycase && typeof timeEntry.mycase === "object"
    ? timeEntry.mycase
    : {};
  const value = [
    timeEntry?.[MYCASE_ACTIVITY_METADATA_KEY],
    timeEntry?.mycaseActivity,
    mycase.activity,
    mycase.activity_name,
  ].find(item => String(item ?? "").trim());

  return value === undefined || value === null ? "" : String(value);
};

export const setMyCaseActivityInMetadata = (metadata, activity) => {
  const next = normalizeTimeEntryMetadata(metadata);
  const value = String(activity ?? "").trim();

  if (value) {
    next[MYCASE_ACTIVITY_METADATA_KEY] = value;
  } else {
    delete next[MYCASE_ACTIVITY_METADATA_KEY];
  }

  return next;
};

export const withMyCaseActivityMetadata = (metadata, activity, enabled) => (
  enabled ? setMyCaseActivityInMetadata(metadata, activity) : normalizeTimeEntryMetadata(metadata)
);

export const withInitialDescriptionMetadata = (metadata, initialDescription) => {
  const next = normalizeTimeEntryMetadata(metadata);
  if (next[INITIAL_DESCRIPTION_METADATA_KEY] != null) return next;

  next[INITIAL_DESCRIPTION_METADATA_KEY] = String(initialDescription ?? "");
  return next;
};

export const stampInitialDescriptionMetadata = (
  metadata,
  narrative,
  capturedDescription
) => {
  const next = normalizeTimeEntryMetadata(metadata);
  if (capturedDescription != null) {
    next[INITIAL_DESCRIPTION_METADATA_KEY] = String(capturedDescription);
    return next;
  }

  return withInitialDescriptionMetadata(next, narrative);
};

export const getMyCaseActivityFromSources = (sources) => (
  (Array.isArray(sources) ? sources : [])
    .map(source => getMyCaseActivityFromTimeEntry(source))
    .find(value => String(value ?? "").trim()) || ""
);

export const withMyCaseActivityFallbackMetadata = (metadata, sources) => {
  const normalized = normalizeTimeEntryMetadata(metadata);
  if (getMyCaseActivityFromMetadata(normalized)) return normalized;

  const activity = getMyCaseActivityFromSources(sources);
  return activity ? setMyCaseActivityInMetadata(normalized, activity) : normalized;
};

export const isMyCaseBillingService = (services) => {
  const list = Array.isArray(services) ? services : [];
  return list.some((service) => {
    const isActiveDestination = String(service?.type ?? "").toLowerCase() === "d" && service?.is_active;
    if (!isActiveDestination) return false;

    return [service?.key, service?.name, service?.service_name, service?.display_name, service?.label, service?.provider, service?.provider_name]
      .some(value => String(value ?? "").toLowerCase().includes("mycase"));
  });
};
